import type { AvailabilityBlock } from "@/lib/availability";
import { statusMeta } from "@/lib/bookings";
import { CALENDAR_STATUS_META, type DayInfo } from "@/pages/admin/calendar/calendarUtils";

interface DayAgendaProps {
  day: DayInfo | null;
  onBlock: (iso: string) => void;
  onRemoveBlock: (block: AvailabilityBlock) => void;
}

export default function DayAgenda({ day, onBlock, onRemoveBlock }: DayAgendaProps) {
  if (!day) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-lg p-5">
        <h3 className="font-heading text-lg text-foreground-950 font-semibold">Select a date</h3>
        <p className="text-sm text-foreground-600 mt-2">
          Pick any day on the calendar to see its bookings, blocks and availability status.
        </p>
      </div>
    );
  }

  const meta = CALENDAR_STATUS_META[day.status];

  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
            {day.date.toLocaleDateString("en-GB", { weekday: "long" })}
          </p>
          <h3 className="font-heading text-xl text-foreground-950 font-semibold">
            {day.date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
          </h3>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${meta.chip}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
          {meta.label}
        </span>
      </div>

      {day.blocks.length > 0 ? (
        <div className="mt-4 space-y-2">
          {day.blocks.map((block) => (
            <div
              key={block.id}
              className="flex items-start justify-between gap-3 rounded-md bg-foreground-100 border border-foreground-200 px-3 py-2.5"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-800 flex items-center gap-1.5">
                  <i className="ri-lock-line"></i>
                  {block.target || "All"} · {block.block_type}
                </p>
                {block.reason ? (
                  <p className="text-xs text-foreground-600 mt-0.5">{block.reason}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => onRemoveBlock(block)}
                aria-label="Remove block"
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 hover:text-foreground-800 transition-colors cursor-pointer shrink-0"
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mt-4 space-y-2">
        {day.bookings.length === 0 ? (
          <p className="text-sm text-foreground-500">No bookings on this date.</p>
        ) : (
          day.bookings.map((booking) => {
            const bookingMeta = statusMeta(booking.status);
            return (
              <div key={booking.id} className="rounded-md bg-background-100 border border-background-200 px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground-950 truncate">{booking.name}</p>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${bookingMeta.badge}`}>
                    {bookingMeta.label}
                  </span>
                </div>
                <p className="text-xs text-foreground-600 mt-0.5 truncate">{booking.subject || "Enquiry"}</p>
                <p className="text-[11px] text-foreground-500 mt-1">
                  {booking.group_size ? `${booking.group_size} guests` : "Group size n/a"}
                </p>
              </div>
            );
          })
        )}
      </div>

      <button
        type="button"
        onClick={() => onBlock(day.iso)}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-calendar-close-line"></i>
        Block this date
      </button>
    </div>
  );
}